import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { BellRing, BellOff, X } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { NOTIFICATIONS_KEY, useNotifications } from "@/pages/app/NotificationsPage";
import { confirmAction } from "@/components/ConfirmDialog";

/**
 * Canlı uyarılar:
 * 1. Web Push: Chrome açıkken panel sekmesi kapalı/arka planda olsa da işletim sistemi bildirimi.
 * 2. Açık sekmede: yeni sipariş/iade bildiriminde sesli uyarı + ekran köşesinde kutu.
 * Bildirim izni yalnız kullanıcı düğmeye bastığında istenir (tarayıcı kuralı).
 */

const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function b64ToUint8(base64: string) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

let audioCtx: AudioContext | null = null;
/** İki tonlu kısa zil (dosya gerekmez). Tarayıcı, sayfayla etkileşimden sonra ses çalmaya izin verir. */
function chime() {
  try {
    audioCtx ??= new AudioContext();
    const ctx = audioCtx;
    void ctx.resume();
    const now = ctx.currentTime;
    [880, 1318.5].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.55);
    });
  } catch {
    /* ses kapalı olabilir */
  }
}

async function registration() {
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

interface Toast {
  id: string;
  title: string;
  body?: string;
  url?: string;
}

export function LiveAlerts() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useNotifications();
  const [state, setState] = useState<"unsupported" | "off" | "on" | "denied" | "busy">(supported ? "off" : "unsupported");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seen = useRef<Set<string> | null>(null);

  const showToast = useCallback((t: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((list) => [...list.slice(-2), { ...t, id }]);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 12_000);
  }, []);

  // Mevcut abonelik durumu.
  useEffect(() => {
    if (!supported) return;
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    void (async () => {
      const reg = await registration();
      const sub = await reg.pushManager.getSubscription();
      if (sub && Notification.permission === "granted") {
        const r = await api.post<{ subscribed: boolean }>("/push/status", { endpoint: sub.endpoint }).catch(() => ({ subscribed: false }));
        if (r.subscribed) setState("on");
        else {
          // Sunucuda kaydı yoksa (ör. başka kullanıcı) yeniden kaydet.
          await api.post("/push/subscribe", sub.toJSON()).catch(() => undefined);
          setState("on");
        }
      }
    })();
  }, []);

  // Service worker'dan gelen push: ses + kutu + listeleri yenile.
  useEffect(() => {
    if (!supported) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== "push") return;
      const p = e.data.payload as { title?: string; body?: string; url?: string; tag?: string };
      if (p.tag) seen.current?.add(p.tag);
      chime();
      showToast({ title: p.title ?? "Yeni bildirim", body: p.body, url: p.url });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [qc, showToast]);

  // Push açık değilse: liste yoklamasında yeni sipariş/iade gelince sesli uyarı.
  useEffect(() => {
    if (!data) return;
    const important = data.items.filter((n) => n.status === "unread" && (n.type === "order.new" || n.type === "return.new"));
    if (!seen.current) {
      seen.current = new Set(important.map((n) => n.id));
      return;
    }
    const fresh = important.filter((n) => !seen.current!.has(n.id));
    for (const n of fresh) seen.current.add(n.id);
    if (fresh.length && state !== "on") {
      chime();
      for (const n of fresh.slice(0, 3)) showToast({ title: n.title, body: n.body ?? undefined, url: n.link ?? undefined });
    }
  }, [data, state, showToast]);

  const enable = async () => {
    setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "denied" : "off");
        return;
      }
      const reg = await registration();
      await navigator.serviceWorker.ready;
      const { publicKey } = await api.get<{ publicKey: string }>("/push/key");
      const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(publicKey) }));
      await api.post("/push/subscribe", sub.toJSON());
      await api.post("/push/test");
      chime();
      setState("on");
    } catch {
      setState("off");
    }
  };

  const disable = async () => {
    setState("busy");
    try {
      const reg = await registration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => undefined);
        await sub.unsubscribe();
      }
    } finally {
      setState("off");
    }
  };

  return (
    <>
      {state === "on" ? (
        <button
          type="button"
          onClick={async () => ((await confirmAction("Bu tarayıcıda masaüstü bildirimleri kapatılsın mı?")) ? void disable() : chime())}
          title="Masaüstü bildirimleri açık · kapatmak için tıklayın"
          className="hidden items-center gap-2 rounded-full border border-success/40 bg-success/10 px-3 py-2 text-xs font-medium text-success sm:inline-flex"
        >
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-success" />
          </span>
          Canlı bildirim açık
        </button>
      ) : state === "unsupported" ? null : state === "denied" ? (
        <span title="Tarayıcı ayarlarından bu site için bildirimlere izin verin" className="hidden items-center gap-1.5 rounded-full border px-3 py-2 text-xs text-muted-foreground sm:inline-flex">
          <BellOff className="size-3.5" /> Bildirim izni kapalı
        </span>
      ) : (
        <button
          type="button"
          onClick={enable}
          disabled={state === "busy"}
          className="inline-flex items-center gap-2 rounded-full bg-accent px-3.5 py-2 text-xs font-semibold text-accent-foreground shadow-sm shadow-accent/30 hover:brightness-105 disabled:opacity-60"
        >
          <BellRing className="size-4" /> Masaüstü bildirimlerini aç
        </button>
      )}

      {createPortal(
      <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-[70] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className={cn("pointer-events-auto flex gap-3 rounded-2xl border border-accent/40 bg-card p-4 shadow-2xl shadow-black/20", "animate-[toastIn_.25s_ease-out]")}>
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
              <BellRing className="size-5" />
            </span>
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => {
                if (t.url) navigate(t.url);
                setToasts((list) => list.filter((x) => x.id !== t.id));
              }}
            >
              <p className="font-semibold">{t.title}</p>
              {t.body && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>}
            </button>
            <button type="button" aria-label="Kapat" className="self-start text-muted-foreground hover:text-foreground" onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}>
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>,
      document.body,
      )}
    </>
  );
}
